/**
 * WorkOrderImport Page (SAP Integration)
 * 
 * PURPOSE: Create cars from SAP production work orders
 * This replaces manual VIN entry with ERP-driven car creation
 * 
 * BUSINESS FLOW:
 * 1. Manager views incoming SAP work orders (API call)
 * 2. Work order contains: VIN, item code, model, color, etc.
 * 3. Click "Create Car" → insert into cars table
 * 4. Link to work instruction via item_code
 * 5. Car appears in operator's list (available for checkout)
 * 
 * SAP API:
 * - Endpoint: https://api-styling-teijin.factory-pilot.ai
 * - Returns: Array of production orders
 * - Fields: DocNum, DocEntry, U_VIN, U_KennNr, ItemCode, etc.
 * 
 * SECURITY:
 * - Only site_manager and quality roles can create cars (RLS enforced)
 * - Frontend check for UX, backend RLS for security
 * 
 * DUPLICATE HANDLING:
 * - Check if VIN already exists at current line
 * - Prevent duplicate car creation
 * - Show friendly error message
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Plus, RefreshCw } from 'lucide-react';

/**
 * SAP Work Order structure (from API)
 * This matches the actual SAP ERP data structure
 */
interface SAPWorkOrder {
  DocNum: number;          // SAP document number
  DocEntry: number;        // SAP internal entry ID
  U_VIN: string;           // Vehicle VIN (primary identifier)
  U_KennNr: string;        // SAP Kennummer (fallback identifier)
  ItemCode: string;        // Item/article code (links to work_instructions)
  ItemName: string;        // Item description (model name)
  Color?: string;          // Vehicle color
  Status?: string;         // Order status
  CreatedAt?: string;      // Order creation date
}

interface WorkOrderImportProps {
  lineId?: string; // Optional - will get from context if not provided
}

const WorkOrderImport = ({ lineId: propLineId }: WorkOrderImportProps) => {
  const { t } = useTranslation();
  const { currentLineId, currentTenantId } = useAuth();
  const lineId = propLineId || currentLineId;
  const { canManage, isReady: rolesReady } = useSiteRoles(lineId || undefined);
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState<SAPWorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState<string | null>(null); // VIN being created

  usePermissionGuard({
    enabled: rolesReady && !!lineId,
    allowed: canManage,
    message: t('dashboard.permissionAccessDenied'),
  });

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  /**
   * Fetch work orders from SAP API
   * 
   * HOW IT WORKS:
   * 1. Call SAP proxy API (defined in sapProxyApi.ts)
   * 2. Filter to open/pending orders
   * 3. Check which VINs already exist in database
   * 4. Mark existing cars (to disable "Create" button)
   * 
   * API STRUCTURE:
   * - Using existing API connection from CarImport page
   * - Endpoint: https://api-styling-teijin.factory-pilot.ai
   * - Returns: Array of work orders with vehicle details
   * 
   * ERROR HANDLING:
   * - If API fails → show error, allow retry
   * - If no orders → show "No pending orders"
   */
  const fetchWorkOrders = async () => {
    setLoading(true);
    try {
      if (!lineId || !currentTenantId) {
        setWorkOrders([]);
        return;
      }
      // Call SAP API (example endpoint - adjust to actual API)
      const response = await fetch('https://api-styling-teijin.factory-pilot.ai/work-orders', {
        headers: {
          'Content-Type': 'application/json',
          // Add auth headers if needed
        },
      });

      if (!response.ok) throw new Error('Failed to fetch work orders');

      const data = await response.json();
      
      // Type assertion (in production, validate structure)
      const orders = data as SAPWorkOrder[];

      // Check which VINs already exist at this line
      // WHY: Prevent duplicate car creation
      const vins = orders.map(o => o.U_VIN || o.U_KennNr).filter(Boolean);
      const { data: existingCars } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .select('vin')
        .eq('tenant_id', currentTenantId)
        .eq('line_id', lineId)
        .in('vin', vins);

      const existingVins = new Set(existingCars?.map(c => c.vin) || []);

      // Mark orders as existing (for UI)
      const ordersWithExistence = orders.map(order => ({
        ...order,
        alreadyExists: existingVins.has(order.U_VIN || order.U_KennNr),
      }));

      setWorkOrders(ordersWithExistence as any);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      toast.error(t('workOrderImport.errors.loadWorkOrders'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create car from SAP work order
   * 
   * BUSINESS LOGIC:
   * 1. Extract VIN (primary) or KennNr (fallback)
   * 2. Check for duplicates (double-check at database level)
   * 3. Insert into cars table with SAP metadata
   * 4. Link to work instruction via item_code
   * 5. Car becomes available for operator checkout
   * 
   * SAP DATA MAPPING:
   * DocNum → sap_doc_num, work_order_id
   * DocEntry → sap_doc_entry
   * U_VIN → vin (primary identifier)
   * U_KennNr → sap_kennummer, vin (fallback if no VIN)
   * ItemCode → item_code (links to work_instructions.item_codes)
   * ItemName → model
   * Color → color
   * 
   * WORK INSTRUCTION LINKING:
   * - work_instructions.item_codes is array of item codes
   * - Use Postgres array contains operator (@>)
   * - Find instruction where item_codes contains this ItemCode
   * - If no match → car still created, but no instruction (handle in UI)
   * 
   * RLS ENFORCEMENT:
    * - Only site_manager and quality can insert cars
    * - RLS policy checks: has_any_role_at_line(line_id, ['site_manager', 'quality'])
   * - If user lacks permission → database returns error
   */
  const handleCreateCar = async (workOrder: SAPWorkOrder) => {
    if (!lineId || !currentTenantId) {
      toast.error(t('workOrderImport.errors.noLineSelected'));
      return;
    }
    // Extract VIN (use U_VIN if available, fallback to U_KennNr)
    const vin = workOrder.U_VIN || workOrder.U_KennNr;

    if (!vin) {
      toast.error(t('workOrderImport.errors.missingVin'));
      return;
    }

    setCreating(vin);

    try {
      // Double-check for duplicate (database also enforces uniqueness)
      const { data: existing } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .select('id')
        .eq('vin', vin)
        .eq('tenant_id', currentTenantId)
        .eq('line_id', lineId)
        .maybeSingle();

      if (existing) {
        toast.error(t('workOrderImport.errors.alreadyExistsAtLine'));
        setCreating(null);
        return;
      }

      // Create car with SAP metadata
      const { data: newCar, error: carError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('cars')
        .insert({
          vin: vin,
          tenant_id: currentTenantId,
          line_id: lineId,
          sap_doc_num: workOrder.DocNum,
          sap_doc_entry: workOrder.DocEntry,
          item_code: workOrder.ItemCode,
          sap_kennummer: workOrder.U_KennNr,
          work_order_id: workOrder.DocNum.toString(),
          model: workOrder.ItemName,
          color: workOrder.Color,
          status: 'pending', // Initial status
        })
        .select()
        .single();

      if (carError) {
        // Check if RLS blocked the insert
        if (carError.code === 'PGRST301' || carError.message.includes('row-level security')) {
          toast.error(t('workOrderImport.errors.noPermission'));
        } else {
          throw carError;
        }
        setCreating(null);
        return;
      }

      // Success - car created
      toast.success(t('workOrderImport.success.carCreated', { vin }));
      
      // Refresh work orders list (mark this one as existing)
      fetchWorkOrders();
    } catch (error) {
      console.error('Error creating car:', error);
      toast.error(t('workOrderImport.errors.createCarFailed'));
    } finally {
      setCreating(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">{t('workOrderImport.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Button variant="outline" onClick={() => navigate('/')} className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('app.backToDashboard')}
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">{t('workOrderImport.title')}</h1>
              <p className="text-muted-foreground">
                {t('workOrderImport.subtitle')}
              </p>
            </div>
            <Button onClick={fetchWorkOrders} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('workOrderImport.refresh')}
            </Button>
          </div>
        </div>

        <Card className="p-6">
          {workOrders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">{t('workOrderImport.empty.title')}</p>
              <p className="text-sm text-muted-foreground mt-2">
                {t('workOrderImport.empty.description')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('workOrderImport.table.orderNumber')}</TableHead>
                  <TableHead>{t('workOrderImport.table.vinKennNr')}</TableHead>
                  <TableHead>{t('workOrderImport.table.itemCode')}</TableHead>
                  <TableHead>{t('workOrderImport.table.model')}</TableHead>
                  <TableHead>{t('workOrderImport.table.color')}</TableHead>
                  <TableHead>{t('workOrderImport.table.status')}</TableHead>
                  <TableHead className="text-right">{t('workOrderImport.table.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workOrders.map((order) => {
                  const vin = order.U_VIN || order.U_KennNr;
                  const alreadyExists = (order as any).alreadyExists;

                  return (
                    <TableRow key={order.DocEntry}>
                      <TableCell className="font-mono">{order.DocNum}</TableCell>
                      <TableCell className="font-mono font-semibold">{vin}</TableCell>
                      <TableCell className="font-mono text-sm">{order.ItemCode}</TableCell>
                      <TableCell>{order.ItemName}</TableCell>
                      <TableCell>{order.Color || '-'}</TableCell>
                      <TableCell>
                        {alreadyExists ? (
                          <Badge variant="outline" className="bg-muted">
                            {t('workOrderImport.status.alreadyImported')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            {t('workOrderImport.status.pending')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => handleCreateCar(order)}
                          disabled={alreadyExists || creating === vin}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          {creating === vin ? t('workOrderImport.creating') : t('workOrderImport.createCar')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Help Section */}
        <div className="mt-8 p-4 bg-muted rounded-lg">
          <h3 className="font-semibold mb-2">{t('workOrderImport.help.title')}</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• {t('workOrderImport.help.line1')}</li>
            <li>• {t('workOrderImport.help.line2')}</li>
            <li>• {t('workOrderImport.help.line3')}</li>
            <li>• {t('workOrderImport.help.line4')}</li>
            <li>• {t('workOrderImport.help.line5')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderImport;
